<template>
  <div
    class="tp-page"
    :class="{ 'tp-page--mobile': isMobile, 'theme--dark': $vuetify.theme.dark }"
  >
    <!-- header -->
    <header class="tp-head">
      <div class="tp-tile">
        <v-icon color="primary">
          mdi-bank-transfer-out
        </v-icon>
      </div>
      <div class="tp-head__text">
        <div class="tp-head__title">
          {{ $t('taxPayerSystem') }}
        </div>
        <div class="tp-head__sub">
          {{ $t('taxPayerSystemHint') }}
        </div>
      </div>
      <span
        v-if="!isMobile && sendingType && methodLabel"
        class="tp-badge"
      >
        <span class="tp-badge__dot" />
        {{ methodLabel }}
      </span>
      <v-btn
        icon
        small
        :aria-label="$t('back')"
        @click="$router.go(-1)"
      >
        <v-icon color="primary">
          mdi-arrow-left
        </v-icon>
      </v-btn>
      <v-progress-linear
        :active="loading"
        indeterminate
        absolute
        bottom
        height="2"
        color="primary"
      />
    </header>

    <v-form
      v-if="render"
      ref="form"
      v-model="valid"
      class="tp-form"
      lazy-validation
      @submit.prevent="create"
    >
      <!-- body -->
      <div class="tp-body">
        <!-- sending method -->
        <section class="tp-card">
          <div class="tp-card__head">
            <div class="tp-tile tp-tile--sm">
              <v-icon
                small
                color="primary"
              >
                mdi-send-circle-outline
              </v-icon>
            </div>
            <div>
              <div class="tp-card__title">
                {{ $t('taxSendingMethod') }}
              </div>
              <div class="tp-card__sub">
                {{ $t('taxSendingMethodHint') }}
              </div>
            </div>
          </div>

          <div class="tp-controls">
            <div class="tp-field-block">
              <span class="tp-mini-label">{{ $t('taxProvider') }}</span>
              <div
                class="tp-segment"
                role="radiogroup"
              >
                <button
                  v-for="m in methods"
                  :key="m.value"
                  type="button"
                  role="radio"
                  :aria-checked="sendingType === m.value ? 'true' : 'false'"
                  class="tp-segment__btn"
                  :class="{ 'tp-segment__btn--on': sendingType === m.value }"
                  @click="sendingType = m.value"
                >
                  <v-icon small>
                    {{ m.icon }}
                  </v-icon>
                  {{ $t(m.value) }}
                </button>
              </div>
            </div>

            <div class="tp-field-block">
              <span class="tp-mini-label">{{ $t('taxPayerTypeLabel') }}</span>
              <div
                class="tp-segment"
                role="radiogroup"
              >
                <button
                  v-for="t in payerTypes"
                  :key="t.value"
                  type="button"
                  role="radio"
                  :aria-checked="taxPayerType === t.value ? 'true' : 'false'"
                  class="tp-segment__btn"
                  :class="{ 'tp-segment__btn--on': taxPayerType === t.value }"
                  @click="taxPayerType = t.value"
                >
                  <v-icon small>
                    {{ t.icon }}
                  </v-icon>
                  {{ $t(t.label) }}
                </button>
              </div>
            </div>
          </div>

          <div
            class="tp-toggle"
            role="switch"
            tabindex="0"
            :aria-checked="autoSending === 'active' ? 'true' : 'false'"
            @click="toggleAuto"
            @keydown.space.prevent="toggleAuto"
            @keydown.enter.prevent="toggleAuto"
          >
            <v-icon
              small
              :color="autoSending === 'active' ? 'primary' : null"
            >
              mdi-autorenew
            </v-icon>
            <span class="tp-toggle__title">{{ $t('sendAutoToTax') }}</span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  x-small
                  class="tp-toggle__info"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-information-outline
                </v-icon>
              </template>
              <span>{{ $t('taxAutoSendHint') }}</span>
            </v-tooltip>
            <v-switch
              :input-value="autoSending === 'active'"
              class="tp-toggle__switch"
              inset
              dense
              hide-details
              readonly
            />
          </div>
        </section>

        <!-- evtsp credentials (legacy method, kept for saved values) -->
        <section
          v-if="sendingType === 'evtsp'"
          class="tp-card"
        >
          <div class="tp-card__head">
            <div class="tp-tile tp-tile--sm">
              <v-icon
                small
                color="primary"
              >
                mdi-account-key-outline
              </v-icon>
            </div>
            <div class="tp-card__title">
              {{ $t('taxCredentials') }}
            </div>
          </div>
          <div class="tp-grid input-minimal">
            <tax-soft-field
              v-model="taxPayerUsername"
              :label="$t('username')"
            />
            <secret-field
              v-model="taxPayerPassword"
              class="dense"
              height="20"
              :label="$t('password')"
            />
          </div>
        </section>

        <!-- identity -->
        <section class="tp-card tp-card--fields">
          <div class="tp-card__head">
            <div class="tp-tile tp-tile--sm">
              <v-icon
                small
                color="primary"
              >
                mdi-card-account-details-outline
              </v-icon>
            </div>
            <div>
              <div class="tp-card__title">
                {{ $t('taxIdentityInfo') }}
              </div>
              <div class="tp-card__sub">
                {{ $t('taxIdentityInfoHint') }}
              </div>
            </div>
          </div>
          <div class="tp-grid tp-grid--3 input-minimal">
            <tax-soft-field
              v-model="nationalCode"
              :label="$t('national-code')"
              copyable
            />
            <tax-soft-field
              v-model="financialCode"
              :label="$t('financial-code')"
              copyable
            />
            <tax-soft-field
              v-model="uniqueIdTaxPayer"
              :label="$t('uniqueIdTaxPayer')"
              copyable
            />
          </div>
        </section>

        <!-- address | private key, side by side -->
        <div
          class="tp-pair"
          :class="{ 'tp-pair--single': sendingType === 'evtsp' }"
        >
        <section class="tp-card">
          <div class="tp-card__head">
            <div class="tp-tile tp-tile--sm">
              <v-icon
                small
                color="primary"
              >
                mdi-map-marker-outline
              </v-icon>
            </div>
            <div class="tp-card__title">
              {{ $t('taxAddressInfo') }}
            </div>
          </div>
          <div class="tp-grid input-minimal">
            <tax-soft-field
              v-model="state"
              :label="$t('state')"
            />
            <tax-soft-field
              v-model="city"
              :label="$t('city')"
            />
            <tax-soft-field
              v-model="address"
              class="tp-span"
              :label="$t('address')"
            />
          </div>
        </section>

        <!-- private key -->
        <section
          v-show="sendingType !== 'evtsp'"
          class="tp-card"
        >
          <div class="tp-card__head">
            <div class="tp-tile tp-tile--sm">
              <v-icon
                small
                color="primary"
              >
                mdi-key-chain-variant
              </v-icon>
            </div>
            <div>
              <div class="tp-card__title">
                {{ $t('privateKeyTaxPayer') }}
              </div>
              <div class="tp-card__sub">
                {{ $t('taxConnectionKeyHint') }}
              </div>
            </div>
            <span
              class="tp-key-state"
              :class="{ 'tp-key-state--on': !!privateKeyTaxPayer }"
            >
              <v-icon x-small>{{ privateKeyTaxPayer ? 'mdi-check-circle' : 'mdi-alert-circle-outline' }}</v-icon>
              {{ privateKeyTaxPayer ? $t('taxKeySet') : $t('taxKeyMissing') }}
            </span>
          </div>
          <tax-soft-field
            v-model="privateKeyTaxPayer"
            :label="$t('privateKeyTaxPayer')"
            copyable
            multiline
          />
        </section>
        </div>
      </div>

      <!-- footer -->
      <footer class="tp-foot">
        <v-btn
          color="primary"
          depressed
          class="tp-submit"
          :block="isMobile"
          :loading="loading"
          type="submit"
        >
          <v-icon
            small
            left
          >
            mdi-check
          </v-icon>
          {{ $t('submit') }}
        </v-btn>
      </footer>
    </v-form>

    <v-snackbar
      v-model="snakbar"
      :color="(snakbarColor ? snakbarColor : 'primary')"
      top
      style="z-index: 1000000000000 !important;"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snakbar = !snakbar"
        >
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import General from '../../../../Mixins/general'
import TaxSoftField from './components/taxPayer/TaxSoftField.vue'
import SecretField from './components/gateway/SecretField.vue'

// Method / payer-type option lists live outside data(): this page posts $data.
const METHODS = [
  { value: 'pna', icon: 'mdi-domain', brandOnly: true },
  { value: 'tiss', icon: 'mdi-server-network', brandOnly: true },
  { value: 'maher', icon: 'mdi-shield-check-outline' },
]
const PAYER_TYPES = [
  { value: 'Legal', label: 'legal', icon: 'mdi-office-building-outline' },
  { value: 'Individual', label: 'Individual', icon: 'mdi-account-outline' },
]

export default {
  name: 'Customer',
  components: {
    TaxSoftField,
    SecretField,
  },
  mixins: [General],
  data: () => ({
    sendingType: 'self',
    taxPayerType: 'legal',
    taxPayerUsername: null,
    taxPayerPassword: null,
    city: null,
    address: null,
    state: null,
    autoSending: null,
    valid: true,
    loading: false,
    render: true,
    nationalCode: null,
    financialCode: null,
    privateKeyTaxPayer: null,
    uniqueIdTaxPayer: null,
    statusTaxPayer: null,
    webServiceAddressTaxPayer: null
  }),
  computed: {
    methods () {
      return METHODS.filter(m => !m.brandOnly || this.$brand === 'abriron')
    },
    payerTypes () {
      return PAYER_TYPES
    },
    methodLabel () {
      return this.methods.some(m => m.value === this.sendingType) ? this.$t(this.sendingType) : ''
    },
  },
  watch: {
  },
  activated () {
    this.show()
    this.fitHeight()
  },
  beforeMount () {
  },
  mounted () {
    this.show()
    this.fitHeight()
    window.addEventListener('resize', this.fitHeight)
    setTimeout(this.fitHeight, 350)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.fitHeight)
  },
  methods: {
    // height lives on the element, not in data() (payload rule)
    fitHeight () {
      const el = this.$el
      if (!el || !el.getBoundingClientRect) return
      const top = el.getBoundingClientRect().top
      el.style.height = Math.max(320, window.innerHeight - top - (this.isMobile ? 0 : 12)) + 'px'
    },
    toggleAuto () {
      this.autoSending = this.autoSending === 'active' ? null : 'active'
    },
    create () {
      if (!this.$refs.form.validate()) return false
      var app = this
      app.loading = true
      app.$axios.post(this.$apipoint + 'v2/tax-payer/register', this.$data)
        .then(res => {
          app.snakbarText = app.$t('registered')
          app.snakbarColor = 'green'
          app.snakbar = true
        })
        .catch(err => {
          this.serverErrorHandeler(err)
        })
        .finally(() => {
          this.loading = false
        })
    },
    show () {
      var app = this
      app.loading = true
      app.$axios.get(this.$endpoint + 'taxPayer', {
        params: {
        }
      })
        .then(res => {
          app.$nextTick(() => {
            res = res.data
            app.taxPayerUsername = res.taxPayerUsername
            app.taxPayerPassword = res.taxPayerPassword
            app.autoSending = res.auto_sending
            app.sendingType = res.sending_type
            app.nationalCode = res.nationalCode
            app.address = res.address
            app.state = res.state
            app.taxPayerType = res.taxPayerType
            app.city = res.city
            app.financialCode = res.financialCode
            app.uniqueIdTaxPayer = res.uniqueIdTaxPayer
            app.webServiceAddressTaxPayer = res.webServiceAddressTaxPayer
            app.privateKeyTaxPayer = res.privateKeyTaxPayer
            app.statusTaxPayer = res.status
            app.render = true
          })
        })
        .catch(err => {
          this.serverErrorHandeler(err)
        })
        .finally(() => (app.loading = false))
    }
  }
}
</script>

<style lang="scss" scoped>
.tp-page {
  display: flex;
  flex-direction: column;
  min-height: 320px;
  background: #f3f7fc;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 6px;
  overflow: hidden;
}

/* header */
.tp-head {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid rgba(15, 23, 42, 0.07);
}
.tp-head__text { flex: 1; min-width: 0; }
.tp-head__title { font-size: .95rem; font-weight: 600; }
.tp-head__sub,
.tp-card__sub { font-size: 0.78rem; opacity: 0.6; }
.tp-tile {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: rgba(25, 118, 210, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}
.tp-tile--sm { width: 28px; height: 28px; }
.tp-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--v-primary-base, #1976d2);
  background: rgba(25, 118, 210, 0.1);
}
.tp-badge__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--v-primary-base, #1976d2);
}

/* body */
.tp-form {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.tp-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.tp-body > * { flex-shrink: 0; }
.tp-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  padding: 10px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.tp-card__head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.tp-card__title { font-size: 0.9rem; font-weight: 600; }

/* sending method / payer type: compact segmented controls in one row */
.tp-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 24px;
}
.tp-field-block { display: flex; flex-direction: column; gap: 4px; }
.tp-mini-label { font-size: 0.74rem; font-weight: 500; opacity: 0.6; }
.tp-segment {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: 6px;
  background: rgba(25, 118, 210, 0.06);
  border: 1px solid rgba(25, 118, 210, 0.1);
}
.tp-segment__btn {
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.84rem;
  white-space: nowrap;
  color: rgba(15, 23, 42, 0.72);
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.tp-segment__btn .v-icon { color: rgba(25, 118, 210, 0.55) !important; }
.tp-segment__btn:hover { background: rgba(25, 118, 210, 0.08); color: #1976d2; }
.tp-segment__btn--on,
.tp-segment__btn--on:hover {
  background: #fff;
  color: var(--v-primary-base, #1976d2);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(25, 118, 210, 0.22);
}
.tp-segment__btn--on .v-icon { color: var(--v-primary-base, #1976d2) !important; }

.tp-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: 6px;
  background: rgba(25, 118, 210, 0.05);
  border: 1px solid rgba(25, 118, 210, 0.1);
  cursor: pointer;
  outline: none;
}
.tp-toggle:focus-visible { box-shadow: inset 0 0 0 2px var(--v-primary-base, #1976d2); }
.tp-toggle__title { font-size: 0.84rem; font-weight: 500; }
.tp-toggle__info { opacity: 0.5; }
.tp-toggle__switch { margin: 0 !important; margin-inline-start: auto !important; padding: 0; pointer-events: none; }
.tp-toggle__switch ::v-deep .v-input__slot { margin: 0; }

/* field grids */
.tp-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 12px;
  padding-top: 4px;
}
.tp-grid--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.tp-span { grid-column: 1 / -1; }
.tp-card--fields { padding-bottom: 24px; }

/* address + private key share one row, equal height */
.tp-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  align-items: stretch;
}
.tp-pair--single { grid-template-columns: minmax(0, 1fr); }
.tp-pair > .tp-card { min-width: 0; }
/* key textarea fills the card instead of spilling under it */
.tp-pair ::v-deep .v-textarea { height: auto !important; flex: 1 1 auto; }

.tp-key-state {
  margin-inline-start: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.74rem;
  color: #e65100;
  background: rgba(255, 152, 0, 0.12);
}
.tp-key-state .v-icon { color: inherit; }
.tp-key-state--on {
  color: #2e7d32;
  background: rgba(76, 175, 80, 0.12);
}

/* footer — submit at start (right in RTL) */
.tp-foot {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: #fff;
  border-top: 1px solid rgba(15, 23, 42, 0.07);
}
.tp-submit {
  min-width: 140px !important;
  height: 36px !important;
  border-radius: 6px !important;
}

/* mobile */
.tp-page--mobile {
  border: 0;
  border-radius: 0;
}
.tp-page--mobile .tp-head { padding: 8px 12px; }
.tp-page--mobile .tp-body { padding: 10px 12px; gap: 10px; }
.tp-page--mobile .tp-card { padding: 10px 12px; }
.tp-page--mobile .tp-pair,
.tp-page--mobile .tp-grid,
.tp-page--mobile .tp-grid--3 { grid-template-columns: minmax(0, 1fr); }
.tp-page--mobile .tp-segment { display: flex; }
.tp-page--mobile .tp-segment__btn { flex: 1; justify-content: center; }
.tp-page--mobile .tp-field-block { width: 100%; }
.tp-page--mobile .tp-foot {
  padding: 8px 12px calc(8px + env(safe-area-inset-bottom));
  box-shadow: 0 -2px 8px rgba(15, 23, 42, 0.06);
}
.tp-page--mobile .tp-submit { height: 44px !important; }

/* dark */
.tp-page.theme--dark {
  background: #121212;
  border-color: rgba(255, 255, 255, 0.08);
}
.theme--dark .tp-head,
.theme--dark .tp-foot,
.theme--dark .tp-card {
  background: #1e1e1e;
  border-color: rgba(255, 255, 255, 0.08);
}
.theme--dark .tp-toggle,
.theme--dark .tp-segment { background: rgba(255, 255, 255, 0.05); }
.theme--dark .tp-segment,
.theme--dark .tp-toggle { border-color: rgba(144, 202, 249, 0.16); }
.theme--dark .tp-segment__btn { color: rgba(255, 255, 255, 0.72); }
.theme--dark .tp-segment__btn .v-icon { color: rgba(144, 202, 249, 0.6) !important; }
.theme--dark .tp-segment__btn--on,
.theme--dark .tp-segment__btn--on:hover { background: rgba(25, 118, 210, 0.28); color: #90caf9; box-shadow: none; }
.theme--dark .tp-segment__btn--on .v-icon { color: #90caf9 !important; }
</style>
